import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import OnboardingLayout from '../../components/layouts/onboarding/OnboardingLayout';
import PrimaryButton from '../../components/common/PrimaryButton';
import InputField from '../../components/common/InputField';
import { colors, typography } from '../../styles/theme/index';
import {
  Form,
  FormContainer,
  FormFields,
  ButtonContainer,
  FormSection,
  CompanyDisplay,
  CompanyValue,
  Label,
  FlexRow
} from '../../styles/components';
import storage from '../../utils/storage';

const CoverLimitsContainer = styled.div`
  width: 100%;
`;

const PropertySection = styled.div`
  width: 100%;
  margin-top: 8px;
`;

const PropertyBlock = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  background: ${colors.background.light};
  border-radius: 8px;
  border: 1px solid ${colors.border};
  width: 100%;
  box-sizing: border-box;

  &:last-child {
    margin-bottom: 8px;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${colors.error};
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.sm};
  cursor: pointer;
  padding: 8px 0;
  margin-top: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const AddPropertyButton = styled.button`
  background: none;
  border: 1px dashed ${colors.border};
  color: ${colors.text.secondary};
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.sm};
  cursor: pointer;
  padding: 12px;
  width: 100%;
  border-radius: 8px;
  margin-top: 4px;

  &:hover {
    color: ${colors.primary};
    border-color: ${colors.primary};
  }
`;

const OnboardingStep7 = () => {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [selectedCovers, setSelectedCovers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [coverLimits, setCoverLimits] = useState({
    publicLiability: '',
    employerLiability: '',
    professionalIndemnity: '',
    directorsAndOfficers: '',
    cyber: '',
    property: '',
    landlord: ''
  });
  const [properties, setProperties] = useState([]);

  const coverLimitOptions = {
    publicLiability: [
      { value: '', label: 'Select a cover limit' },
      { value: '1000000', label: '£1,000,000' },
      { value: '2000000', label: '£2,000,000' },
      { value: '5000000', label: '£5,000,000' },
      { value: '10000000', label: '£10,000,000' },
      { value: 'other', label: 'Other' }
    ],
    employerLiability: [
      { value: '', label: 'Select a cover limit' },
      { value: '10000000', label: '£10,000,000' },
      { value: 'other', label: 'Other' }
    ],
    professionalIndemnity: [
      { value: '', label: 'Select a cover limit' },
      { value: '500000', label: '£500,000' },
      { value: '1000000', label: '£1,000,000' },
      { value: '2000000', label: '£2,000,000' },
      { value: 'other', label: 'Other' }
    ],
    directorsAndOfficers: [
      { value: '', label: 'Select a cover limit' },
      { value: '1000000', label: '£1,000,000' },
      { value: '2000000', label: '£2,000,000' },
      { value: '5000000', label: '£5,000,000' },
      { value: 'other', label: 'Other' }
    ],
    cyber: [
      { value: '', label: 'Select a cover limit' },
      { value: '500000', label: '£500,000' },
      { value: '1000000', label: '£1,000,000' },
      { value: '2000000', label: '£2,000,000' },
      { value: 'other', label: 'Other' }
    ]
  };

  useEffect(() => {
    // Load company name from step 3
    const step3Data = storage.getItem('step3Data');
    if (step3Data) {
      try {
        const { companyName } = JSON.parse(step3Data);
        setCompanyName(companyName);
      } catch (error) {
        console.error('Error parsing step 3 data:', error);
      }
    }

    // Load selected covers from step 6
    const step6Data = storage.getItem('step6Data');
    if (step6Data) {
      try {
        const { selectedCovers: savedCovers } = JSON.parse(step6Data);
        setSelectedCovers(savedCovers || []);
      } catch (error) {
        console.error('Error parsing step 6 data:', error);
        setSelectedCovers([]);
      }
    }

    // Load organization ID from Pipedrive data
    const pipedriveData = storage.getItem('pipedriveData');
    if (pipedriveData) {
      try {
        const { organizationId } = JSON.parse(pipedriveData);
        if (organizationId) {
          setOrganizationId(organizationId);
        } else {
          console.error('No organization ID found in Pipedrive data');
        }
      } catch (error) {
        console.error('Error parsing Pipedrive data:', error);
      }
    }

    // Load previously saved cover limits and properties
    const step7Data = storage.getItem('step7Data');
    if (step7Data) {
      try {
        const parsedData = JSON.parse(step7Data);
        
        // Initialize with default empty values
        const defaultCoverLimits = {
          publicLiability: '',
          employerLiability: '',
          professionalIndemnity: '',
          directorsAndOfficers: '',
          cyber: '',
          property: '',
          landlord: ''
        };

        // Load cover limits if they exist
        if (parsedData.coverLimits) {
          setCoverLimits({
            ...defaultCoverLimits,
            ...parsedData.coverLimits
          });
        }

        // Load properties if they exist
        if (parsedData.properties) {
          setProperties(parsedData.properties);
        }
      } catch (error) {
        console.error('Error parsing step 7 data:', error);
        // Set default empty values if there's an error
        setCoverLimits({
          publicLiability: '',
          employerLiability: '',
          professionalIndemnity: '',
          directorsAndOfficers: '',
          cyber: '',
          property: '',
          landlord: ''
        });
        setProperties([]);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validate that at least one cover limit is set
      const hasSelectedLimit = Object.values(coverLimits).some(value => value !== '');
      if (!hasSelectedLimit) {
        throw new Error('Please select at least one cover limit');
      }

      // Filter out empty cover limits
      const filteredCoverLimits = Object.fromEntries(
        Object.entries(coverLimits).filter(([_, value]) => value !== '')
      );

      // Prepare the data for submission including both cover limits and properties
      const submitData = {
        organizationId,
        coverLimits: filteredCoverLimits,
        properties: properties.length > 0 ? properties : undefined
      };

      // Submit all data to backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/onboarding/step-seven`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to submit cover limits');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to submit cover limits');
      }

      // Store form data
      storage.setItem('step7Data', JSON.stringify({
        coverLimits,
        properties
      }));

      // Navigate to next step
      navigate('/onboarding/step-eight');
    } catch (error) {
      console.error('Form submission error:', error);
      setError(error.message || 'There was an error processing your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCoverLimitChange = (coverType, value) => {
    const newLimits = {
      ...coverLimits,
      [coverType]: value
    };
    setCoverLimits(newLimits);
    // Save immediately when cover limits change
    storage.setItem('step7Data', JSON.stringify({
      coverLimits: newLimits,
      properties
    }));
  };

  const handlePropertyChange = (index, field, value) => {
    const newProperties = [...properties];
    newProperties[index] = {
      ...newProperties[index],
      [field]: value
    };
    setProperties(newProperties);
    // Save immediately when property details change
    storage.setItem('step7Data', JSON.stringify({
      coverLimits,
      properties: newProperties
    }));
  };

  const handleAddProperty = () => {
    const newProperties = [...properties, { rebuildValue: '', postcode: '' }];
    setProperties(newProperties);
    // Save immediately when adding a property
    storage.setItem('step7Data', JSON.stringify({
      coverLimits,
      properties: newProperties
    }));
  };

  const handleRemoveProperty = (index) => {
    const newProperties = properties.filter((_, i) => i !== index);
    setProperties(newProperties);
    // Save immediately when removing a property
    storage.setItem('step7Data', JSON.stringify({
      coverLimits,
      properties: newProperties
    }));
  };

  const handleBack = () => {
    // Store all step 7 data before navigating back
    storage.setItem('step7Data', JSON.stringify({
      coverLimits,
      properties
    }));
    navigate('/onboarding/step-six');
  };

  return (
    <OnboardingLayout
      title="Choose your"
      brandText="Cover Limits"
      onBack={handleBack}
    >
      <FormContainer>
        <CompanyDisplay>
          <CompanyValue>{companyName}</CompanyValue>
        </CompanyDisplay>

        <Form onSubmit={handleSubmit}>
          <FormFields>
            <CoverLimitsContainer>
              {selectedCovers.includes('Public Liability') && (
                
                  <InputField
                    label="Public Liability Maximum"
                    name="publicLiability"
                    as="select"
                    value={coverLimits.publicLiability}
                    onChange={(e) => handleCoverLimitChange('publicLiability', e.target.value)}
                  >
                    {coverLimitOptions.publicLiability.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </InputField>
                
              )}

              {selectedCovers.includes('Employer Liability') && (
                
                  <InputField
                    label="Employer Liability Maximum"
                    name="employerLiability"
                    as="select"
                    value={coverLimits.employerLiability}
                    onChange={(e) => handleCoverLimitChange('employerLiability', e.target.value)}
                  >
                    {coverLimitOptions.employerLiability.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </InputField>
                
              )}

              {selectedCovers.includes('Professional Indemnity') && (
                
                  <InputField
                    label="Professional Indemnity Maximum"
                    name="professionalIndemnity"
                    as="select"
                    value={coverLimits.professionalIndemnity}
                    onChange={(e) => handleCoverLimitChange('professionalIndemnity', e.target.value)}
                  >
                    {coverLimitOptions.professionalIndemnity.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </InputField>
                
              )}

              {selectedCovers.includes('Directors and Officers') && (
                
                  <InputField
                    label="Directors and Officers Maximum"
                    name="directorsAndOfficers"
                    as="select"
                    value={coverLimits.directorsAndOfficers}
                    onChange={(e) => handleCoverLimitChange('directorsAndOfficers', e.target.value)}
                  >
                    {coverLimitOptions.directorsAndOfficers.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </InputField>
                
              )}

              {selectedCovers.includes('Cyber') && (
                
                  <InputField
                    label="Cyber Insurance Maximum"
                    name="cyber"
                    as="select"
                    value={coverLimits.cyber}
                    onChange={(e) => handleCoverLimitChange('cyber', e.target.value)}
                  >
                    {coverLimitOptions.cyber.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </InputField>
                
              )}

              {selectedCovers.includes('Property') && (
                
                  <InputField
                    label="Property Insurance Maximum"
                    name="property"
                    as="select"
                    value={coverLimits.property}
                    onChange={(e) => handleCoverLimitChange('property', e.target.value)}
                  >
                    {coverLimitOptions.property.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </InputField>
                
              )}

              {selectedCovers.includes('Landlord') && (
                
                  <InputField
                    label="Landlord Insurance Maximum"
                    name="landlord"
                    as="select"
                    value={coverLimits.landlord}
                    onChange={(e) => handleCoverLimitChange('landlord', e.target.value)}
                  >
                    {coverLimitOptions.landlord.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </InputField>
                
              )}

              {selectedCovers.includes('Property / Landlord') && (
                <PropertySection>
                  <Label>
                    Property / Landlord 
                  </Label>
                  {properties.map((property, index) => (
                    <PropertyBlock key={index}>
                      <FlexRow>
                        <div style={{ flex: 1 }}>
                          <InputField
                            label="Rebuild Value"
                            type="text"
                            value={property.rebuildValue}
                            onChange={(e) => handlePropertyChange(index, 'rebuildValue', e.target.value)}
                            placeholder="Enter rebuild value"
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <InputField
                            label="Postcode"
                            type="text"
                            value={property.postcode}
                            onChange={(e) => handlePropertyChange(index, 'postcode', e.target.value)}
                            placeholder="Enter postcode"
                          />
                        </div>
                      </FlexRow>
                      <RemoveButton
                        type="button"
                        onClick={() => handleRemoveProperty(index)}
                      >
                        Remove Property
                      </RemoveButton>
                    </PropertyBlock>
                  ))}
                  <AddPropertyButton
                    type="button"
                    onClick={handleAddProperty}
                  >
                    + Add Property
                  </AddPropertyButton>
                </PropertySection>
              )}
            </CoverLimitsContainer>
          </FormFields>

          <ButtonContainer>
            <PrimaryButton type="submit">Continue</PrimaryButton>
          </ButtonContainer>
        </Form>
      </FormContainer>
    </OnboardingLayout>
  );
};

export default OnboardingStep7; 